/**
 * Wire types for 重点保障中心 (#2583, modes #2602).
 *
 * SKU ultimate fallback is `off|shadow|act`. Org×SKU overlays are
 * `inherit|off|shadow|act`. The write face still accepts legacy `on`
 * and stores it as `act`. Resolution: org overlay if not inherit, else
 * the SKU row, else off. The request path walks only for `act`.
 */
import type { RoutingCacheConvergence } from './internal-tenant-routing.ts';

export const GUARANTEED_SCOPES = ['sku', 'org_sku'] as const;
export type GuaranteedScope = (typeof GUARANTEED_SCOPES)[number];

export const GUARANTEED_MODES = ['off', 'shadow', 'act'] as const;
export type GuaranteedMode = (typeof GUARANTEED_MODES)[number];

/** Write-face values. `on` is accepted for one release and stored as `act`. */
export const GUARANTEED_SKU_SWITCHES = ['off', 'shadow', 'act', 'on'] as const;
export type GuaranteedSkuSwitch = (typeof GUARANTEED_SKU_SWITCHES)[number];

export const GUARANTEED_JEV_MODES = ['off', 'shadow', 'act'] as const;
export type GuaranteedJevMode = (typeof GUARANTEED_JEV_MODES)[number];

/** Write-face overlay values. `on` is accepted and stored as `act`. */
export const GUARANTEED_OVERRIDES = ['inherit', 'off', 'shadow', 'act', 'on'] as const;
export type GuaranteedOverride = (typeof GUARANTEED_OVERRIDES)[number];

export function isGuaranteedScope(value: unknown): value is GuaranteedScope {
  return value === 'sku' || value === 'org_sku';
}

export function isGuaranteedMode(value: unknown): value is GuaranteedMode {
  return value === 'off' || value === 'shadow' || value === 'act';
}

export function isGuaranteedSkuSwitch(value: unknown): value is GuaranteedSkuSwitch {
  return value === 'off' || value === 'shadow' || value === 'act' || value === 'on';
}

/** `on` → `act`. Anything else that is not a mode returns null. */
export function canonicalGuaranteedMode(value: unknown): GuaranteedMode | null {
  if (value === 'on') return 'act';
  return isGuaranteedMode(value) ? value : null;
}

/** Jev trip criteria bounds (per SKU, #2602): N qualifying verdicts inside W seconds. */
export const GUARANTEED_JEV_TRIP_MIN_HITS_DEFAULT = 5;
export const GUARANTEED_JEV_TRIP_MIN_HITS_MIN = 1;
export const GUARANTEED_JEV_TRIP_MIN_HITS_MAX = 100;
export const GUARANTEED_JEV_TRIP_WINDOW_SECONDS_DEFAULT = 300;
export const GUARANTEED_JEV_TRIP_WINDOW_SECONDS_MIN = 10;
export const GUARANTEED_JEV_TRIP_WINDOW_SECONDS_MAX = 3600;

/**
 * Minutes between scheduled probe rounds (`probe_interval_minutes`, 1–1440).
 * New rows start at 15 since #2658 tuning (0088 moves the column default
 * too; existing rows keep theirs): at 3 shots a round, the quality window
 * holds 24 shots instead of 12.
 */
export const GUARANTEED_PROBE_INTERVAL_MINUTES_DEFAULT = 15;
export const GUARANTEED_PROBE_INTERVAL_MINUTES_MIN = 1;
export const GUARANTEED_PROBE_INTERVAL_MINUTES_MAX = 1440;

/**
 * Probe round criteria bounds (per SKU, user 2026-09-22). Defaults 3 shots /
 * 2 failed rounds since #2658 follow-up (2026-09-24): a two-shot, one-round
 * trip fired on a single ~16 s upstream stall.
 */
export const GUARANTEED_PROBE_SHOTS_PER_ROUND_DEFAULT = 3;
export const GUARANTEED_PROBE_SHOTS_PER_ROUND_MAX = 5;
export const GUARANTEED_PROBE_TRIP_FAILED_ROUNDS_DEFAULT = 2;
export const GUARANTEED_PROBE_TRIP_FAILED_ROUNDS_MAX = 10;
export const GUARANTEED_PROBE_RECOVER_OK_ROUNDS_DEFAULT = 2;
export const GUARANTEED_PROBE_RECOVER_OK_ROUNDS_MAX = 10;
/** Seconds between the shots of one round, so a short stall cannot catch them all. 0 = back to back. */
export const GUARANTEED_PROBE_SHOT_SPACING_SECONDS_DEFAULT = 20;
export const GUARANTEED_PROBE_SHOT_SPACING_SECONDS_MIN = 0;
export const GUARANTEED_PROBE_SHOT_SPACING_SECONDS_MAX = 60;
/**
 * Per-shot abort. A slower answer counts as a failed shot. 20 s since the
 * slow line moved to 16 s (#2658, 0089): the abort must sit above the slow
 * line — the write face refuses `probe_slow_ms` over it — and the margin lets
 * a shot that answers after 16 s still log its real latency.
 */
export const GUARANTEED_PROBE_SHOT_TIMEOUT_SECONDS_DEFAULT = 20;
export const GUARANTEED_PROBE_SHOT_TIMEOUT_SECONDS_MIN = 5;
export const GUARANTEED_PROBE_SHOT_TIMEOUT_SECONDS_MAX = 60;
/**
 * After a failed round that is still below the trip line, the next round is
 * due this many seconds after it ends instead of a full interval. Capped at
 * the SKU's interval at runtime (the write face refuses a larger value).
 */
export const GUARANTEED_PROBE_CONFIRM_AFTER_SECONDS_DEFAULT = 120;
export const GUARANTEED_PROBE_CONFIRM_AFTER_SECONDS_MIN = 30;
/** The longest probe interval (1440 minutes). */
export const GUARANTEED_PROBE_CONFIRM_AFTER_SECONDS_MAX = 86_400;

/**
 * The slow line cannot sit above the per-shot abort: a shot is aborted at
 * the timeout, so a 2xx slower than that line is never seen (the write face
 * refuses it — 400 INVALID_PAYLOAD — when a write sets either field, or
 * creates the row).
 */
export function guaranteedProbeSlowFitsTimeout(slowMs: number, timeoutSeconds: number): boolean {
  return slowMs <= timeoutSeconds * 1000;
}

/** Database lease / settle time on top of a round's shots and pauses. */
export const GUARANTEED_PROBE_ROUND_MARGIN_SECONDS = 30;

/**
 * Longest a scheduled round can take: every shot timing out, the pauses
 * between them, and the settle margin. A leg leased less than this ago is
 * not picked again (so a confirmation round is never sooner than this after
 * the round started), and the write face refuses criteria whose round would
 * not fit in the probe interval.
 */
export function guaranteedProbeRoundBudgetSeconds(criteria: {
  readonly shots: number;
  readonly spacingSeconds: number;
  readonly timeoutSeconds: number;
}): number {
  const shots = Math.max(1, criteria.shots);
  return shots * criteria.timeoutSeconds + (shots - 1) * criteria.spacingSeconds + GUARANTEED_PROBE_ROUND_MARGIN_SECONDS;
}

/**
 * Probe QUALITY signal (#2658 follow-up, 2026-09-24). The round rule above
 * is the outage signal (every shot of a round fails); it filters bursts, so
 * an upstream that stalls for a few seconds every 15–30 minutes passes it
 * forever. The quality signal trips a leg whose bad-shot share over a
 * sliding window reaches `trip_pct` (at least `min_shots` shots), and lifts
 * it only when the share is at most `recover_pct` AND the last
 * `probe_recover_ok_rounds` rounds fully passed.
 *
 * A shot is bad when it is non-2xx, unreachable / timed out, an empty 2xx,
 * or slower than `probe_slow_ms`. The slow line also fails the shot for the
 * round (outage) rule, so a longer shot timeout does not loosen judgement.
 *
 * Defaults from the production replay (2026-09-24): 120 minutes / 12 shots
 * holds at the default cadence (3 shots every 30 minutes = 12 shots in 120
 * minutes); a 60-minute window holds only 6 there and one bad shot would be
 * 17 %.
 *
 * Slow line 16 s since #2658 (user 2026-09-26: 「宽容点」; 0089 moves the
 * column default, existing rows keep theirs). It must not exceed the shot
 * timeout (`guaranteedProbeSlowFitsTimeout`).
 */
export const GUARANTEED_PROBE_SLOW_MS_DEFAULT = 16_000;
export const GUARANTEED_PROBE_SLOW_MS_MIN = 1_000;
export const GUARANTEED_PROBE_SLOW_MS_MAX = 30_000;
export const GUARANTEED_PROBE_QUALITY_WINDOW_MINUTES_DEFAULT = 120;
export const GUARANTEED_PROBE_QUALITY_WINDOW_MINUTES_MIN = 15;
export const GUARANTEED_PROBE_QUALITY_WINDOW_MINUTES_MAX = 360;
export const GUARANTEED_PROBE_QUALITY_MIN_SHOTS_DEFAULT = 12;
export const GUARANTEED_PROBE_QUALITY_MIN_SHOTS_MIN = 3;
export const GUARANTEED_PROBE_QUALITY_MIN_SHOTS_MAX = 100;
export const GUARANTEED_PROBE_QUALITY_TRIP_PCT_DEFAULT = 15;
export const GUARANTEED_PROBE_QUALITY_TRIP_PCT_MIN = 1;
export const GUARANTEED_PROBE_QUALITY_TRIP_PCT_MAX = 100;
export const GUARANTEED_PROBE_QUALITY_RECOVER_PCT_DEFAULT = 5;
export const GUARANTEED_PROBE_QUALITY_RECOVER_PCT_MIN = 0;
/**
 * Staging rehearsal 2026-09-24 (F2): with 12 shots in the window, 2 bad
 * shots (16 %) tripped a healthy leg, and recovery waited for the bad shots
 * to age out of the 120-minute window (1 bad shot in 16 is 6 % > 5 %).
 * A trip also needs at least `min_bad_shots` bad shots; recovery judges
 * only the last `recover_window_minutes` (at most the quality window's
 * shots count, see the gateway's `qualityRecoverMinShots`).
 */
export const GUARANTEED_PROBE_QUALITY_MIN_BAD_SHOTS_DEFAULT = 3;
export const GUARANTEED_PROBE_QUALITY_MIN_BAD_SHOTS_MIN = 1;
export const GUARANTEED_PROBE_QUALITY_MIN_BAD_SHOTS_MAX = 100;
export const GUARANTEED_PROBE_QUALITY_RECOVER_WINDOW_MINUTES_DEFAULT = 30;
export const GUARANTEED_PROBE_QUALITY_RECOVER_WINDOW_MINUTES_MIN = 5;
export const GUARANTEED_PROBE_QUALITY_RECOVER_WINDOW_MINUTES_MAX = 360;
/** Recovery needs at least min(this, the shots the recover window can hold at the SKU interval). */
export const GUARANTEED_PROBE_QUALITY_RECOVER_MIN_SHOTS = 6;

/**
 * Probe levels per region × physical leg (#2658 follow-up, user
 * 2026-09-24: 常规 / 警戒 / 战争, 「三个层级都有摘的能力，但是对应的是不同的频率、
 * 危险层级」). A trigger — real-traffic failures on the leg (the chain-trip
 * counters: non-2xx minus known client faults, 60 s) at `min_failures`
 * (twice that on a fallback, weight 0 leg), or one Jev upstream-fault
 * verdict — takes the leg to WAR (the concurrent-wave burst below); a war
 * ends in ALERT (rounds every `interval_seconds`, never below the round
 * budget); alert returns to NORMAL after `calm_minutes` with no trigger and
 * no failing round. After a war the leg cannot re-enter war for
 * min(base × factor^(wars in the reset window − 1), max) minutes; a trigger
 * while cooling keeps it in alert. Each level can trip: rounds (and the
 * quality signal) at normal and alert cadence, two bad waves in war.
 */
export const GUARANTEED_PROBE_ALERT_WINDOW_SECONDS = 60;
export const GUARANTEED_PROBE_ALERT_FALLBACK_MULTIPLIER = 2;
export const GUARANTEED_PROBE_ALERT_MIN_FAILURES_DEFAULT = 3;
export const GUARANTEED_PROBE_ALERT_MIN_FAILURES_MIN = 1;
export const GUARANTEED_PROBE_ALERT_MIN_FAILURES_MAX = 100;
/**
 * Red-team P2-1: the line was absolute only, so a leg serving thousands of
 * requests a minute went to war on 3 failures (0.1 %). The trigger also
 * needs this share of the leg's counted attempts in the window to fail
 * (failures ÷ (failures + 2xx); 429 / 408 are neither).
 */
export const GUARANTEED_PROBE_ALERT_MIN_FAIL_PCT_DEFAULT = 50;
export const GUARANTEED_PROBE_ALERT_MIN_FAIL_PCT_MIN = 1;
export const GUARANTEED_PROBE_ALERT_MIN_FAIL_PCT_MAX = 100;
/**
 * 15 s fast ratio lane (#2658 tuning). The 60 s share above waits for the
 * pre-storm 2xx to age out of the window (~30 s at common traffic); a
 * leg whose failures also reach the 60 s count line (2x on a fallback leg)
 * triggers when its last 15 s (three 5 s buckets) hold at least
 * `FAST_MIN_FAILURES` failures that are at least `FAST_MIN_FAIL_PCT` of the
 * counted attempts there. Same exclusions as the main rule (429 / 408,
 * known client faults, tenant hang-ups) and the same two-host gate.
 * Constants on purpose: production replay (7 days, 45 legs) put the war
 * rate at 34 -> 37 a week and p50 detection at >= 15 RPM at 41 -> 21 s.
 */
export const GUARANTEED_PROBE_ALERT_FAST_WINDOW_SECONDS = 15;
export const GUARANTEED_PROBE_ALERT_FAST_MIN_FAILURES = 3;
export const GUARANTEED_PROBE_ALERT_FAST_MIN_FAIL_PCT = 60;
/**
 * First-byte-late signal (#2658 tuning, 0088; 0 = off). A STREAMED attempt
 * already sent upstream that has not received its first byte this many ms
 * later counts one failure toward the war trigger (60 s count and share, and
 * the 15 s lane) — once per attempt, never again when it later fails. The
 * request is not interrupted and its result, billing, the breaker, the chain
 * trip and Jev are unaffected. Non-stream requests are never counted (their
 * first byte is the whole answer). Per SKU: fast models ~15 000 ms; leave it
 * off for reasoning / large models. The gateway's first-byte TIMEOUT
 * (`attempt_first_byte_timeout_ms`, load settings) is a separate knob.
 */
export const GUARANTEED_PROBE_ALERT_FIRST_BYTE_LATE_MS_DEFAULT = 0;
export const GUARANTEED_PROBE_ALERT_FIRST_BYTE_LATE_MS_MIN = 1_000;
export const GUARANTEED_PROBE_ALERT_FIRST_BYTE_LATE_MS_MAX = 300_000;
/** The value the console suggests for fast models. */
export const GUARANTEED_PROBE_ALERT_FIRST_BYTE_LATE_MS_SUGGESTED = 15_000;
/**
 * 130 s = the default round budget (3 × 20 s + 2 × 20 s + 30 s) since the
 * shot timeout moved to 20 s (#2658, 0089). The runtime never runs alert
 * rounds closer than one round budget anyway, and the write face refuses a
 * written alert interval below it, so a 120 s default was refused on
 * restore and ran at 130 s regardless.
 */
export const GUARANTEED_PROBE_ALERT_INTERVAL_SECONDS_DEFAULT = 130;
export const GUARANTEED_PROBE_ALERT_INTERVAL_SECONDS_MIN = 60;
export const GUARANTEED_PROBE_ALERT_INTERVAL_SECONDS_MAX = 1800;
export const GUARANTEED_PROBE_ALERT_CALM_MINUTES_DEFAULT = 10;
export const GUARANTEED_PROBE_ALERT_CALM_MINUTES_MIN = 1;
export const GUARANTEED_PROBE_ALERT_CALM_MINUTES_MAX = 120;
export const GUARANTEED_PROBE_WAR_COOLDOWN_BASE_MINUTES_DEFAULT = 5;
export const GUARANTEED_PROBE_WAR_COOLDOWN_BASE_MINUTES_MIN = 1;
export const GUARANTEED_PROBE_WAR_COOLDOWN_BASE_MINUTES_MAX = 120;
export const GUARANTEED_PROBE_WAR_COOLDOWN_FACTOR_DEFAULT = 2;
export const GUARANTEED_PROBE_WAR_COOLDOWN_FACTOR_MIN = 1;
export const GUARANTEED_PROBE_WAR_COOLDOWN_FACTOR_MAX = 10;
export const GUARANTEED_PROBE_WAR_COOLDOWN_MAX_MINUTES_DEFAULT = 60;
export const GUARANTEED_PROBE_WAR_COOLDOWN_MAX_MINUTES_MAX = 1440;
export const GUARANTEED_PROBE_WAR_BACKOFF_RESET_MINUTES_DEFAULT = 60;
export const GUARANTEED_PROBE_WAR_BACKOFF_RESET_MINUTES_MIN = 10;
export const GUARANTEED_PROBE_WAR_BACKOFF_RESET_MINUTES_MAX = 1440;

/**
 * Burst probing, the fast front of the escalation (user 2026-09-24:
 * 「急促模式可以并发，比如四发，更快，也许能做到 30 秒 20 秒内完成处置」). On a
 * trigger (the real-traffic alert line, or one Jev upstream-fault verdict)
 * the leg gets WAVES of `wave_shots` concurrent shots, each aborted at the
 * slow line; wave N+1 starts `wave_every_seconds` after wave N started, or
 * when wave N's shots are all back if that is later (waves never overlap). A
 * wave is bad when `wave_fail_shots` of its shots fail; the last
 * `waves_to_trip` waves all bad trip (act) or shadow-trip (shadow) with
 * reason `probe` — with the defaults ~11 s after the trigger when the shots
 * fail at once (an error answer), ~32 s when they hang to the 16 s slow line
 * (the second wave then starts at 16 s, not 10 s). Waves
 * are spaced so one short stall cannot make two of them bad; the shots of a
 * wave sample the same instant. `exit_good_waves` good waves in a row, or
 * `max_seconds` (wall clock: past it no wave starts, except up to
 * `waves_to_trip - 1` verdict waves while the last wave was bad — so the
 * last of several wars on one hung upstream account still reaches its
 * verdict), end it (a trip can happen at any time before that);
 * recovery uses the round rules. One process bursts a leg at a time.
 */
/**
 * No-response cutoff (#2744, user 2026-09-30). Per SKU, null = off (the
 * default). When set, a request on the SKU whose upstream attempt has
 * produced no ACTUAL CONTENT this many ms after it was sent is cut and the
 * next leg of the chain is tried, like a retryable network failure. Only a
 * chunk carrying generated text, reasoning, a tool call, an error event or a
 * whole JSON body is content. Response headers, SSE comments / keep-alives
 * (`: PING`), blank lines and opening chunks that carry none of those (OpenAI
 * chat role-only / empty-string deltas; Responses `response.created` /
 * `in_progress` / `queued` / `metadata`, an empty message item, an empty
 * content part;
 * Anthropic `message_start` / `ping`, an empty text `content_block_start`)
 * are not — the empty opening of a text block or message does not count, but
 * reasoning or thinking is content the moment it starts (a reasoning item
 * added, a thinking block started, however empty); an unknown or
 * unparseable block is content. The
 * LAST leg is never cut, and once content has reached the tenant nothing is
 * cut.
 *
 * A cut is recorded (lifecycle phase, a zero-cost `-aN` usage row with
 * status 504, a per-minute `no_response_cutoff` load event) but is NOT a
 * failure: it does not count
 * toward the breaker, the load snapshot's errors / timeouts, the smart score,
 * the probe war trigger, the chain trip, Jev or session rehome. Independent
 * of `ultimate_fallback` and of the load settings'
 * `attempt_first_byte_timeout_ms`. Meaningful on SKU rows only; org×SKU rows
 * keep null.
 *
 * On a lane that converts protocols (a Responses / codex upstream served to
 * a chat or Anthropic tenant, an Anthropic thinking upstream served to a chat
 * tenant) the upstream's own wire is watched too (#2753): content on either
 * wire counts, so a started reasoning item / thinking block is content even
 * though the converter sends the tenant nothing for it. The upstream side
 * counts positive evidence only (a delta, a reasoning / tool item, a thinking
 * or tool block, a terminal or error event); unknown frames and long
 * openings do not clear the cutoff. Residual gaps: an upstream whose own wire
 * carries no reasoning marker at all (e.g. o-series hiding its reasoning on
 * the chat wire — only the opening and keep-alives until the answer) looks
 * like a stall and is cut — do not turn it on for such SKUs; and the
 * load-aware first-byte timeout (`attempt_first_byte_timeout_ms`, off in
 * production) is a separate, older timer that can still cut a silently
 * reasoning leg on a SKU that also sets it.
 *
 * A probabilistic trade-off, not a guarantee: the cut leg is walked past, but
 * the legs after it can still fail on their own, and then the tenant gets
 * that failure. The last leg is never cut, nor is a smart-routed request. A
 * stream is judged by its first actual content; a non-stream answer must be
 * complete within the cutoff, or a non-last leg is cut — except where the
 * gateway streams from the upstream and aggregates (codex, forced-stream
 * lanes): there the upstream's first actual content counts.
 */
export const GUARANTEED_NO_RESPONSE_CUTOFF_MIN_MS = 10_000;
/**
 * 120 s: a non-last leg sends the tenant no header while it is held, and the
 * edge in front of the gateway drops an origin that is silent for ~100 s, so a
 * longer cutoff could never fire before the tenant is gone (review 2026-09-30).
 */
export const GUARANTEED_NO_RESPONSE_CUTOFF_MAX_MS = 120_000;

/** Null (off) or an integer 10000–120000 ms. */
export function isGuaranteedNoResponseCutoffMs(value: unknown): value is number | null {
  return value === null
    || (typeof value === 'number' && Number.isSafeInteger(value)
      && value >= GUARANTEED_NO_RESPONSE_CUTOFF_MIN_MS && value <= GUARANTEED_NO_RESPONSE_CUTOFF_MAX_MS);
}

export const GUARANTEED_PROBE_BURST_WAVE_SHOTS_DEFAULT = 4;
export const GUARANTEED_PROBE_BURST_WAVE_SHOTS_MIN = 1;
export const GUARANTEED_PROBE_BURST_WAVE_SHOTS_MAX = 8;
export const GUARANTEED_PROBE_BURST_WAVE_EVERY_SECONDS_DEFAULT = 10;
export const GUARANTEED_PROBE_BURST_WAVE_EVERY_SECONDS_MIN = 5;
export const GUARANTEED_PROBE_BURST_WAVE_EVERY_SECONDS_MAX = 60;
export const GUARANTEED_PROBE_BURST_WAVE_FAIL_SHOTS_DEFAULT = 3;
export const GUARANTEED_PROBE_BURST_WAVE_FAIL_SHOTS_MIN = 1;
export const GUARANTEED_PROBE_BURST_WAVES_TO_TRIP_DEFAULT = 2;
export const GUARANTEED_PROBE_BURST_WAVES_TO_TRIP_MIN = 1;
export const GUARANTEED_PROBE_BURST_WAVES_TO_TRIP_MAX = 5;
/** Good waves in a row that end the burst early (user: 持续久一点，两三分钟都可以). */
export const GUARANTEED_PROBE_BURST_EXIT_GOOD_WAVES_DEFAULT = 6;
export const GUARANTEED_PROBE_BURST_EXIT_GOOD_WAVES_MIN = 1;
export const GUARANTEED_PROBE_BURST_EXIT_GOOD_WAVES_MAX = 30;
export const GUARANTEED_PROBE_BURST_MAX_SECONDS_DEFAULT = 180;
export const GUARANTEED_PROBE_BURST_MAX_SECONDS_MIN = 60;
export const GUARANTEED_PROBE_BURST_MAX_SECONDS_MAX = 600;

function isIntIn(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
}

export function isGuaranteedProbeShotsPerRound(value: unknown): value is number {
  return isIntIn(value, 1, GUARANTEED_PROBE_SHOTS_PER_ROUND_MAX);
}

export function isGuaranteedProbeTripFailedRounds(value: unknown): value is number {
  return isIntIn(value, 1, GUARANTEED_PROBE_TRIP_FAILED_ROUNDS_MAX);
}

export function isGuaranteedProbeRecoverOkRounds(value: unknown): value is number {
  return isIntIn(value, 1, GUARANTEED_PROBE_RECOVER_OK_ROUNDS_MAX);
}

export function isGuaranteedProbeShotSpacingSeconds(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_SHOT_SPACING_SECONDS_MIN, GUARANTEED_PROBE_SHOT_SPACING_SECONDS_MAX);
}

export function isGuaranteedProbeShotTimeoutSeconds(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_SHOT_TIMEOUT_SECONDS_MIN, GUARANTEED_PROBE_SHOT_TIMEOUT_SECONDS_MAX);
}

export function isGuaranteedProbeConfirmAfterSeconds(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_CONFIRM_AFTER_SECONDS_MIN, GUARANTEED_PROBE_CONFIRM_AFTER_SECONDS_MAX);
}

export function isGuaranteedProbeSlowMs(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_SLOW_MS_MIN, GUARANTEED_PROBE_SLOW_MS_MAX);
}

export function isGuaranteedProbeQualityWindowMinutes(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_QUALITY_WINDOW_MINUTES_MIN, GUARANTEED_PROBE_QUALITY_WINDOW_MINUTES_MAX);
}

export function isGuaranteedProbeQualityMinShots(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_QUALITY_MIN_SHOTS_MIN, GUARANTEED_PROBE_QUALITY_MIN_SHOTS_MAX);
}

export function isGuaranteedProbeQualityTripPct(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_QUALITY_TRIP_PCT_MIN, GUARANTEED_PROBE_QUALITY_TRIP_PCT_MAX);
}

export function isGuaranteedProbeQualityMinBadShots(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_QUALITY_MIN_BAD_SHOTS_MIN, GUARANTEED_PROBE_QUALITY_MIN_BAD_SHOTS_MAX);
}

export function isGuaranteedProbeQualityRecoverWindowMinutes(value: unknown): value is number {
  return isIntIn(
    value,
    GUARANTEED_PROBE_QUALITY_RECOVER_WINDOW_MINUTES_MIN,
    GUARANTEED_PROBE_QUALITY_RECOVER_WINDOW_MINUTES_MAX,
  );
}

/** 0 up to the trip line: recovery must sit strictly on the good side of it or at it. */
export function isGuaranteedProbeQualityRecoverPct(value: unknown, tripPct: number): value is number {
  return isIntIn(value, GUARANTEED_PROBE_QUALITY_RECOVER_PCT_MIN, tripPct);
}

export function isGuaranteedProbeAlertMinFailures(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_ALERT_MIN_FAILURES_MIN, GUARANTEED_PROBE_ALERT_MIN_FAILURES_MAX);
}

export function isGuaranteedProbeAlertMinFailPct(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_ALERT_MIN_FAIL_PCT_MIN, GUARANTEED_PROBE_ALERT_MIN_FAIL_PCT_MAX);
}

/** 0 (off) or 1000–300000 ms. */
export function isGuaranteedProbeAlertFirstByteLateMs(value: unknown): value is number {
  return value === 0
    || isIntIn(value, GUARANTEED_PROBE_ALERT_FIRST_BYTE_LATE_MS_MIN, GUARANTEED_PROBE_ALERT_FIRST_BYTE_LATE_MS_MAX);
}

export function isGuaranteedProbeAlertIntervalSeconds(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_ALERT_INTERVAL_SECONDS_MIN, GUARANTEED_PROBE_ALERT_INTERVAL_SECONDS_MAX);
}

export function isGuaranteedProbeAlertCalmMinutes(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_ALERT_CALM_MINUTES_MIN, GUARANTEED_PROBE_ALERT_CALM_MINUTES_MAX);
}

export function isGuaranteedProbeWarCooldownBaseMinutes(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_WAR_COOLDOWN_BASE_MINUTES_MIN, GUARANTEED_PROBE_WAR_COOLDOWN_BASE_MINUTES_MAX);
}

export function isGuaranteedProbeWarCooldownFactor(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_WAR_COOLDOWN_FACTOR_MIN, GUARANTEED_PROBE_WAR_COOLDOWN_FACTOR_MAX);
}

/** The base cooldown up to 1440 minutes. */
export function isGuaranteedProbeWarCooldownMaxMinutes(value: unknown, baseMinutes: number): value is number {
  return isIntIn(value, baseMinutes, GUARANTEED_PROBE_WAR_COOLDOWN_MAX_MINUTES_MAX);
}

export function isGuaranteedProbeWarBackoffResetMinutes(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_WAR_BACKOFF_RESET_MINUTES_MIN, GUARANTEED_PROBE_WAR_BACKOFF_RESET_MINUTES_MAX);
}

export function isGuaranteedProbeBurstWaveShots(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_BURST_WAVE_SHOTS_MIN, GUARANTEED_PROBE_BURST_WAVE_SHOTS_MAX);
}

export function isGuaranteedProbeBurstWaveEverySeconds(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_BURST_WAVE_EVERY_SECONDS_MIN, GUARANTEED_PROBE_BURST_WAVE_EVERY_SECONDS_MAX);
}

/** 1 up to the wave's shots. */
export function isGuaranteedProbeBurstWaveFailShots(value: unknown, waveShots: number): value is number {
  return isIntIn(value, GUARANTEED_PROBE_BURST_WAVE_FAIL_SHOTS_MIN, waveShots);
}

export function isGuaranteedProbeBurstWavesToTrip(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_BURST_WAVES_TO_TRIP_MIN, GUARANTEED_PROBE_BURST_WAVES_TO_TRIP_MAX);
}

export function isGuaranteedProbeBurstExitGoodWaves(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_BURST_EXIT_GOOD_WAVES_MIN, GUARANTEED_PROBE_BURST_EXIT_GOOD_WAVES_MAX);
}

export function isGuaranteedProbeBurstMaxSeconds(value: unknown): value is number {
  return isIntIn(value, GUARANTEED_PROBE_BURST_MAX_SECONDS_MIN, GUARANTEED_PROBE_BURST_MAX_SECONDS_MAX);
}

export function isGuaranteedJevTripMinHits(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value)
    && value >= GUARANTEED_JEV_TRIP_MIN_HITS_MIN && value <= GUARANTEED_JEV_TRIP_MIN_HITS_MAX;
}

export function isGuaranteedJevTripWindowSeconds(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value)
    && value >= GUARANTEED_JEV_TRIP_WINDOW_SECONDS_MIN && value <= GUARANTEED_JEV_TRIP_WINDOW_SECONDS_MAX;
}

export function isGuaranteedJevMode(value: unknown): value is GuaranteedJevMode {
  return value === 'off' || value === 'shadow' || value === 'act';
}

export const GUARANTEED_PROBE_SHAPES = ['ping', 'arithmetic', 'echo', 'custom'] as const;
export type GuaranteedProbeShape = (typeof GUARANTEED_PROBE_SHAPES)[number];

export function isGuaranteedProbeShape(value: unknown): value is GuaranteedProbeShape {
  return value === 'ping' || value === 'arithmetic' || value === 'echo' || value === 'custom';
}

export function isGuaranteedOverride(value: unknown): value is GuaranteedOverride {
  return value === 'inherit' || value === 'off' || value === 'shadow' || value === 'act' || value === 'on';
}

/** Stored overlay value. Legacy `on` becomes `act`. */
export function canonicalGuaranteedOverride(value: unknown): 'inherit' | GuaranteedMode | null {
  if (value === 'inherit') return 'inherit';
  return canonicalGuaranteedMode(value);
}

/** Resolved values consumed by the gateway and by #2585. Overlay jev is ignored. */
export interface GuaranteedServiceResolved {
  readonly jev_judge: GuaranteedJevMode;
  /** True only when the effective mode is `act` (legacy `on` included). */
  readonly ultimate_fallback: boolean;
  readonly ultimate_fallback_mode: GuaranteedMode;
}

export interface GuaranteedServiceSkuEntry {
  readonly id: string;
  readonly scope: 'sku';
  readonly org_id: null;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly jev_judge: GuaranteedJevMode;
  readonly ultimate_fallback: GuaranteedMode;
  readonly probe_mode: GuaranteedMode;
  readonly probe_enabled: boolean;
  /** Minutes between probe rounds. Integer 1–1440; default 15 (was 30 before 0088). */
  readonly probe_interval_minutes: number;
  /** Non-stream user-message probe. Default `ping`. Meaningful on SKU rows. */
  readonly probe_shape: GuaranteedProbeShape;
  /** Required when `probe_shape` is `custom`. Null for the other shapes. */
  readonly probe_custom_text: string | null;
  /** 1–64 when set. Null uses 16. Null for non-custom shapes. */
  readonly probe_custom_max_tokens: number | null;
  readonly chain_trip_mode: GuaranteedMode;
  readonly chain_trip: boolean;
  /** Probe round criteria (user 2026-09-22): shots per round (1–5, default 3)… */
  readonly probe_shots_per_round: number;
  /** …consecutive failed rounds that trip (1–10, default 2)… */
  readonly probe_trip_failed_rounds: number;
  /** …consecutive passing rounds that recover (1–10, default 2). */
  readonly probe_recover_ok_rounds: number;
  /** Seconds between the shots of a scheduled round (0–60, default 20). */
  readonly probe_shot_spacing_seconds: number;
  /** Per-shot timeout in seconds (5–60, default 15). */
  readonly probe_shot_timeout_seconds: number;
  /** Next round after a failed round below the trip line (30 s – interval, default 120). */
  readonly probe_confirm_after_seconds: number;
  /** A 2xx slower than this (ms) is a bad shot, for the round and quality rules (1000–30000, default 8000). */
  readonly probe_slow_ms: number;
  /** Quality window in minutes (15–360, default 120)… */
  readonly probe_quality_window_minutes: number;
  /** …judged only with at least this many shots in it (3–100, default 12)… */
  readonly probe_quality_min_shots: number;
  /** …trips at this bad-shot percentage or more (1–100, default 15)… */
  readonly probe_quality_trip_pct: number;
  /** …with at least this many bad shots (1–100, default 3)… */
  readonly probe_quality_min_bad_shots: number;
  /** …and recovers at this percentage or less plus the ok-round line (0–trip, default 5)… */
  readonly probe_quality_recover_pct: number;
  /** …judged over only the last this-many minutes (5–360, default 30). */
  readonly probe_quality_recover_window_minutes: number;
  /** Real-traffic failures in 60 s that take a first-traffic leg to war (1–100, default 3; fallback legs 2×)… */
  readonly probe_alert_min_failures: number;
  /** …when also at least this share of the leg's counted attempts in the window failed (1–100 %, default 50). */
  readonly probe_alert_min_fail_pct: number;
  /** A streamed attempt with no first byte after this many ms counts one war-trigger failure (0 = off, else 1000–300000; default 0). */
  readonly probe_alert_first_byte_late_ms: number;
  /** Alert level: a round every this many seconds (60–1800, default 120, at least the round budget)… */
  readonly probe_alert_interval_seconds: number;
  /** …back to normal after this long with no trigger and no failing round (1–120 minutes, default 10). */
  readonly probe_alert_calm_minutes: number;
  /** Burst: waves of this many concurrent shots (1–8, default 4)… */
  readonly probe_burst_wave_shots: number;
  /** …one wave every this many seconds from the previous wave's start (5–60, default 10)… */
  readonly probe_burst_wave_every_seconds: number;
  /** …a wave is bad when this many of its shots fail (1–wave shots, default 3)… */
  readonly probe_burst_wave_fail_shots: number;
  /** …this many bad waves in a row trip (1–5, default 2)… */
  readonly probe_burst_waves_to_trip: number;
  /** …this many good waves in a row end it early (1–30, default 6)… */
  readonly probe_burst_exit_good_waves: number;
  /** …the burst ends after this long at most (60–600 s, default 180). */
  readonly probe_burst_max_seconds: number;
  /** War cooldown: base minutes (1–120, default 5)… */
  readonly probe_war_cooldown_base_minutes: number;
  /** …× this factor per earlier war in the reset window (1–10, default 2)… */
  readonly probe_war_cooldown_factor: number;
  /** …capped at this (base–1440 minutes, default 60)… */
  readonly probe_war_cooldown_max_minutes: number;
  /** …and the war count resets after this long without a war (10–1440 minutes, default 60). */
  readonly probe_war_backoff_reset_minutes: number;
  /** Jev trips after this many qualifying verdicts on one leg… (1–100, default 5). */
  readonly jev_trip_min_hits: number;
  /** …inside this window (10–3600 s, default 300). Also the verdict dedupe window. */
  readonly jev_trip_window_seconds: number;
  /** No-response cutoff in ms (#2744): null = off, else 10000–120000. See {@link GUARANTEED_NO_RESPONSE_CUTOFF_MIN_MS}. */
  readonly no_response_cutoff_ms: number | null;
  readonly note: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly request_count_24h: number;
  readonly entered_chain_count_24h: number;
  readonly chain_exhausted_count_24h: number;
  /** `guaranteed_shadow_enter` rows in the last 24 hours. */
  readonly shadow_enter_24h: number;
  /**
   * Failed attempts that Jev (shadow / act) did not judge in the last
   * `JEV_SKIP_SUMMARY_MINUTES`, by reason, both regions summed; reasons with
   * none are absent. Absent when the summary could not be read.
   */
  readonly jev_skips_1h?: JevSkipCounts;
}

/**
 * Why a failed attempt on a SKU with Jev on (shadow / act) was not handed
 * to Jev (#2658: without a trace an operator could not tell a Jev that
 * never ran from one with nothing to judge):
 *
 * - `tenant_aborted`: the tenant hung up; the failure may be ours.
 * - `media_lane`: an image / video lane SKU (Jev judges text requests).
 * - `rate_limit_or_timeout`: 408, 429 or 504 — the breaker's, not Jev's.
 * - `status_not_judged`: another 5xx than 500 / 502 / 503.
 * - `client_fault`: a known tenant fault (context too long, moderation…).
 * - `queue_full`: Jev's in-process queue was full.
 */
export const JEV_SKIP_REASONS = [
  'tenant_aborted',
  'media_lane',
  'rate_limit_or_timeout',
  'status_not_judged',
  'client_fault',
  'queue_full',
] as const;
export type JevSkipReason = (typeof JEV_SKIP_REASONS)[number];
export type JevSkipCounts = Readonly<Partial<Record<JevSkipReason, number>>>;

export function isJevSkipReason(value: unknown): value is JevSkipReason {
  return typeof value === 'string' && (JEV_SKIP_REASONS as readonly string[]).includes(value);
}

/** A gateway process writes its Jev skip counts at most this often (one row per region × SKU). */
export const JEV_SKIP_FLUSH_SECONDS = 300;
/** The console's Jev skip summary covers this many minutes. */
export const JEV_SKIP_SUMMARY_MINUTES = 60;

/**
 * `details_json` of a `jev_skipped` load event: one gateway process's
 * skipped counts for one SKU in one region over `[window_start,
 * window_end)`. Only SKUs with Jev on are counted; zero reasons are left
 * out; a window with nothing skipped writes no row.
 */
export interface JevSkippedEventDetails {
  readonly counts: JevSkipCounts;
  readonly window_start: string;
  readonly window_end: string;
}

export interface GuaranteedServiceOrgSkuEntry {
  readonly id: string;
  readonly scope: 'org_sku';
  readonly org_id: string;
  readonly sku_id: string;
  readonly sku_name: string;
  readonly jev_judge: GuaranteedOverride;
  readonly ultimate_fallback: 'inherit' | GuaranteedMode;
  readonly effective: GuaranteedServiceResolved;
  readonly note: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly request_count_24h: number;
  readonly entered_chain_count_24h: number;
  readonly chain_exhausted_count_24h: number;
  readonly shadow_enter_24h: number;
}

export type GuaranteedServiceEntry = GuaranteedServiceSkuEntry | GuaranteedServiceOrgSkuEntry;

export interface ListGuaranteedServiceEntriesResponse {
  readonly sku: readonly GuaranteedServiceSkuEntry[];
  readonly org_sku: readonly GuaranteedServiceOrgSkuEntry[];
}

export interface CreateGuaranteedServiceSkuRequest {
  readonly scope: 'sku';
  readonly sku_ids: readonly string[];
  /** Omitted means `off`. */
  readonly jev_judge?: GuaranteedJevMode;
  /** Omitted means `off`. `on` is stored as `act`. */
  readonly ultimate_fallback?: GuaranteedSkuSwitch;
  readonly probe_mode?: GuaranteedMode;
  readonly probe_enabled?: boolean;
  readonly probe_interval_minutes?: number;
  readonly probe_shape?: GuaranteedProbeShape;
  readonly probe_custom_text?: string | null;
  readonly probe_custom_max_tokens?: number | null;
  readonly chain_trip_mode?: GuaranteedMode;
  readonly chain_trip?: boolean;
  readonly probe_shots_per_round?: number;
  readonly probe_trip_failed_rounds?: number;
  readonly probe_recover_ok_rounds?: number;
  readonly probe_shot_spacing_seconds?: number;
  readonly probe_shot_timeout_seconds?: number;
  readonly probe_confirm_after_seconds?: number;
  readonly probe_slow_ms?: number;
  readonly probe_quality_window_minutes?: number;
  readonly probe_quality_min_shots?: number;
  readonly probe_quality_trip_pct?: number;
  readonly probe_quality_min_bad_shots?: number;
  readonly probe_quality_recover_pct?: number;
  readonly probe_quality_recover_window_minutes?: number;
  readonly probe_alert_min_failures?: number;
  readonly probe_alert_min_fail_pct?: number;
  readonly probe_alert_first_byte_late_ms?: number;
  readonly probe_alert_interval_seconds?: number;
  readonly probe_alert_calm_minutes?: number;
  readonly probe_burst_wave_shots?: number;
  readonly probe_burst_wave_every_seconds?: number;
  readonly probe_burst_wave_fail_shots?: number;
  readonly probe_burst_waves_to_trip?: number;
  readonly probe_burst_exit_good_waves?: number;
  readonly probe_burst_max_seconds?: number;
  readonly probe_war_cooldown_base_minutes?: number;
  readonly probe_war_cooldown_factor?: number;
  readonly probe_war_cooldown_max_minutes?: number;
  readonly probe_war_backoff_reset_minutes?: number;
  readonly jev_trip_min_hits?: number;
  readonly jev_trip_window_seconds?: number;
  /** Omitted: unchanged (create: null). Null turns it off; else 10000–120000 ms. */
  readonly no_response_cutoff_ms?: number | null;
  readonly note?: string | null;
}

export interface CreateGuaranteedServiceOrgSkuRequest {
  readonly scope: 'org_sku';
  readonly org_id: string;
  readonly sku_ids: readonly string[];
  /** Omitted means `inherit`. `on` is stored as `act`. */
  readonly ultimate_fallback?: GuaranteedOverride;
  readonly note?: string | null;
}

export type CreateGuaranteedServiceRequest =
  | CreateGuaranteedServiceSkuRequest
  | CreateGuaranteedServiceOrgSkuRequest;

export interface CreateGuaranteedServiceResponse {
  readonly items: readonly GuaranteedServiceEntry[];
  /**
   * Cache convergence in the region that ran the write (#2658). Optional:
   * an older gateway omits it. Says nothing about the other region, which
   * receives the row by logical replication — see the propagation read.
   */
  readonly cache?: RoutingCacheConvergence;
}

export interface PatchGuaranteedServiceSkuRequest {
  readonly jev_judge?: GuaranteedJevMode;
  readonly ultimate_fallback?: GuaranteedSkuSwitch;
  readonly probe_mode?: GuaranteedMode;
  readonly probe_enabled?: boolean;
  readonly probe_interval_minutes?: number;
  readonly probe_shape?: GuaranteedProbeShape;
  readonly probe_custom_text?: string | null;
  readonly probe_custom_max_tokens?: number | null;
  readonly chain_trip_mode?: GuaranteedMode;
  readonly chain_trip?: boolean;
  readonly probe_shots_per_round?: number;
  readonly probe_trip_failed_rounds?: number;
  readonly probe_recover_ok_rounds?: number;
  readonly probe_shot_spacing_seconds?: number;
  readonly probe_shot_timeout_seconds?: number;
  readonly probe_confirm_after_seconds?: number;
  readonly probe_slow_ms?: number;
  readonly probe_quality_window_minutes?: number;
  readonly probe_quality_min_shots?: number;
  readonly probe_quality_trip_pct?: number;
  readonly probe_quality_min_bad_shots?: number;
  readonly probe_quality_recover_pct?: number;
  readonly probe_quality_recover_window_minutes?: number;
  readonly probe_alert_min_failures?: number;
  readonly probe_alert_min_fail_pct?: number;
  readonly probe_alert_first_byte_late_ms?: number;
  readonly probe_alert_interval_seconds?: number;
  readonly probe_alert_calm_minutes?: number;
  readonly probe_burst_wave_shots?: number;
  readonly probe_burst_wave_every_seconds?: number;
  readonly probe_burst_wave_fail_shots?: number;
  readonly probe_burst_waves_to_trip?: number;
  readonly probe_burst_exit_good_waves?: number;
  readonly probe_burst_max_seconds?: number;
  readonly probe_war_cooldown_base_minutes?: number;
  readonly probe_war_cooldown_factor?: number;
  readonly probe_war_cooldown_max_minutes?: number;
  readonly probe_war_backoff_reset_minutes?: number;
  readonly jev_trip_min_hits?: number;
  readonly jev_trip_window_seconds?: number;
  /** Omitted: unchanged (create: null). Null turns it off; else 10000–120000 ms. */
  readonly no_response_cutoff_ms?: number | null;
  readonly note?: string | null;
}

export interface PatchGuaranteedServiceOrgSkuRequest {
  readonly ultimate_fallback?: GuaranteedOverride;
  readonly note?: string | null;
}

export type PatchGuaranteedServiceRequest =
  | PatchGuaranteedServiceSkuRequest
  | PatchGuaranteedServiceOrgSkuRequest;

export interface PatchGuaranteedServiceResponse {
  readonly item: GuaranteedServiceEntry;
  /** See {@link CreateGuaranteedServiceResponse.cache}. */
  readonly cache?: RoutingCacheConvergence;
}

export interface DeleteGuaranteedServiceResponse {
  readonly deleted: true;
  /** See {@link CreateGuaranteedServiceResponse.cache}. */
  readonly cache?: RoutingCacheConvergence;
}

/**
 * Gateway `GET /internal/guaranteed-service/:id` (#2658): what THIS region's
 * database holds for one entry. Cheap on purpose — the console polls it on
 * both regions after a write to confirm the downlink delivered it. A missing
 * row is `present: false` with 200, so an older gateway without the route
 * (404) stays distinguishable from "not replicated yet".
 */
export interface GuaranteedServiceEntryState {
  readonly id: string;
  readonly present: boolean;
  /** The answering gateway's `GATEWAY_REGION` slug (`jp`, `cn`). */
  readonly region: string;
  readonly updated_at: string | null;
  readonly scope: GuaranteedScope | null;
  readonly jev_judge: string | null;
  readonly ultimate_fallback: string | null;
  readonly probe_mode: string | null;
  readonly chain_trip_mode: string | null;
}

function nullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

export function parseGuaranteedServiceEntryState(raw: unknown): GuaranteedServiceEntryState | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const record: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) record[key] = value;
  const {
    id, present, region, updated_at: updatedAt, scope,
    jev_judge: jev, ultimate_fallback: fallback, probe_mode: probe, chain_trip_mode: chain,
  } = record;
  if (typeof id !== 'string' || typeof present !== 'boolean' || typeof region !== 'string') return null;
  if (!nullableString(updatedAt) || !nullableString(jev) || !nullableString(fallback)) return null;
  if (!nullableString(probe) || !nullableString(chain)) return null;
  const scopeValue = scope === null ? null : isGuaranteedScope(scope) ? scope : undefined;
  if (scopeValue === undefined) return null;
  if (present && (updatedAt === null || !Number.isFinite(Date.parse(updatedAt)))) return null;
  return {
    id,
    present,
    region,
    updated_at: updatedAt,
    scope: scopeValue,
    jev_judge: jev,
    ultimate_fallback: fallback,
    probe_mode: probe,
    chain_trip_mode: chain,
  };
}

/**
 * Per-region read-back state (#2658), relative to the `updated_at` the write
 * returned. `match`: the region holds that version or a later one. `pending`:
 * it holds an older version. `missing`: no row (the goal after a delete; "not
 * delivered yet" after a create). `unreachable`: the region did not answer in
 * time or answered garbage. `not_configured`: this environment has no such
 * region wired into ops-api (dev has no CN).
 */
export const GUARANTEED_PROPAGATION_STATES = [
  'match',
  'pending',
  'missing',
  'unreachable',
  'not_configured',
] as const;
export type GuaranteedPropagationState = (typeof GUARANTEED_PROPAGATION_STATES)[number];

export interface GuaranteedServiceRegionPropagation {
  readonly state: GuaranteedPropagationState;
  readonly updated_at: string | null;
}

export interface GuaranteedServicePropagationResponse {
  readonly id: string;
  /** Echo of `?updated_at=`; null when the caller asked for presence only. */
  readonly expected_updated_at: string | null;
  readonly jp: GuaranteedServiceRegionPropagation;
  readonly cn: GuaranteedServiceRegionPropagation;
}

/** Pure comparison used by ops-api; `expected` null means "present at all". */
export function guaranteedPropagationState(
  entry: GuaranteedServiceEntryState,
  expectedUpdatedAt: string | null,
): GuaranteedPropagationState {
  if (!entry.present || entry.updated_at === null) return 'missing';
  if (expectedUpdatedAt === null) return 'match';
  const expected = Date.parse(expectedUpdatedAt);
  const held = Date.parse(entry.updated_at);
  if (!Number.isFinite(expected) || !Number.isFinite(held)) return 'pending';
  return held >= expected ? 'match' : 'pending';
}

export interface GuaranteedServiceTextSku {
  readonly sku_id: string;
  readonly sku_name: string;
}

export interface ListGuaranteedServiceTextSkusResponse {
  readonly skus: readonly GuaranteedServiceTextSku[];
}

/**
 * Pure two-layer resolution (#2583). Org overlay wins when not `inherit`;
 * otherwise the SKU row; otherwise both switches off.
 *
 * Stable for #2585: that Issue consumes `jev_judge` from this function
 * and must not re-derive the ladder.
 */
export function resolveGuaranteedService(params: {
  readonly sku: {
    readonly jev_judge: GuaranteedJevMode;
    readonly ultimate_fallback: GuaranteedSkuSwitch;
  } | null;
  readonly orgSku: {
    readonly ultimate_fallback: GuaranteedOverride;
  } | null;
}): GuaranteedServiceResolved {
  const chosen = params.orgSku !== null && params.orgSku.ultimate_fallback !== 'inherit'
    ? params.orgSku.ultimate_fallback
    : (params.sku?.ultimate_fallback ?? 'off');
  const mode = canonicalGuaranteedMode(chosen) ?? 'off';
  return {
    jev_judge: params.sku?.jev_judge ?? 'off',
    ultimate_fallback: mode === 'act',
    ultimate_fallback_mode: mode,
  };
}

export const GUARANTEED_EVENT_PAGE_SIZE = 20;
export const GUARANTEED_EVENT_MAX_PAGE_SIZE = 100;

export interface GuaranteedServiceEventRecord {
  readonly event_ts: string;
  readonly kind: string;
  readonly sku_id: string | null;
  readonly upstream_id: string | null;
  readonly upstream_name: string | null;
  readonly account_id: string | null;
  readonly key_bucket_id: string | null;
  readonly upstream_channel_id: string | null;
  readonly org_id: string | null;
  readonly region: string;
  readonly details: Readonly<Record<string, unknown>> | null;
  readonly source: 'load_event' | 'verdict';
}

export interface ListGuaranteedServiceEventsResponse {
  readonly events: readonly GuaranteedServiceEventRecord[];
  readonly total: number;
  readonly page: number;
  readonly page_size: number;
}
